class MobileGallery extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.isScrolling = false;
    this.resizeTimer = null; // Timer for resize debounce
  }

  connectedCallback() {
    // 1. Store original content once on load
    if (!this.originalContent) {
      this.originalContent = this.innerHTML;
    }

    // 2. Run initialization
    this.init();

    // 3. Listen for resize with delay (Debounce) to optimize performance
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.init();
      }, 150);
    });
  }

  init() {
    const isMobile = window.innerWidth <= 767;
    const hasSlider = this.querySelector('.mobile-gallery__main') !== null;
    const loaderEnabled = this.classList.contains('mobile-loader-enabled');

    // Handle single slide mode (disable slider logic if this parent class exists)
    if (this.closest('.mobile-single-slide')) {
      if (hasSlider) this.destroy();
      this.style.display = 'block';
      return;
    }

    if (isMobile) {
      // Mobile version: Build the slider structure if it doesn't exist yet
      if (!hasSlider) {
        this.render();
        this.bindEvents();
        this.setActiveImage(0);
      }

      // If the loader feature is enabled, signal that the mobile block is initialized
      if (loaderEnabled) {
        setTimeout(() => {
          this.classList.add('mobile-loader-ready');
        }, 200);
      }

      this.style.display = 'block';
    } else {
      // Desktop version: If the slider exists, destroy it and restore original HTML
      if (hasSlider) {
        this.destroy();

        // Remove the ready class when switching back to desktop
        if (loaderEnabled) {
          this.classList.remove('mobile-loader-ready');
        }
      }
      this.style.display = 'contents';
    }
  }

  render() {
    // If already rendered - exit
    if (this.querySelector('.mobile-gallery__main')) return;

    // IMPORTANT: Get elements from originalContent, not current DOM,
    // because current DOM might be empty or modified after a destroy/init cycle.
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = this.originalContent;
    const mediaItems = Array.from(tempDiv.querySelectorAll('.media-item'));

    if (mediaItems.length === 0) return;

    // Create main image container
    const mainContainer = document.createElement('div');
    mainContainer.className = 'mobile-gallery__main';
    mainContainer.setAttribute('role', 'list');

    // Create thumbnails container
    const thumbsContainer = document.createElement('div');
    thumbsContainer.className = 'mobile-gallery__thumbs';
    thumbsContainer.setAttribute('role', 'list');

    // Process media items
    mediaItems.forEach((item, index) => {
      // Clone for main image
      const mainItem = item.cloneNode(true);
      mainItem.className = 'mobile-gallery__main-item';
      mainItem.setAttribute('role', 'listitem');
      mainItem.dataset.index = index;
      if (index === 0) mainItem.classList.add('active');
      mainContainer.appendChild(mainItem);

      // Create thumbnail
      const thumb = document.createElement('div');
      thumb.className = 'mobile-gallery__thumb';
      thumb.setAttribute('role', 'listitem');
      thumb.dataset.index = index;
      if (index === 0) thumb.classList.add('active');

      // Handle media content for thumbnail
      const media = item.querySelector('img, video');
      if (media) {
        if (media.tagName === 'VIDEO') {
          const thumbImg = document.createElement('img');
          if (media.poster) {
            thumbImg.src = media.poster;
            thumbImg.alt = media.alt || 'Video thumbnail';
          } else {
            // Placeholder SVG
            thumbImg.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iNCIgZmlsbD0iIzMzMzMzMyIvPgo8cGF0aCBkPSJNMTYgMTJMMjggMjBMMTYgMjhWMTJaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
            thumbImg.alt = 'Video';
          }
          thumb.appendChild(thumbImg);
        } else {
          const thumbMedia = media.cloneNode(true);
          thumb.appendChild(thumbMedia);
        }
      }
      thumbsContainer.appendChild(thumb);
    });

    // Clear container and append new structure
    this.innerHTML = '';
    this.appendChild(mainContainer);
    this.appendChild(thumbsContainer);
  }

  destroy() {
    // Restore original HTML for desktop
    if (this.originalContent) {
      this.innerHTML = this.originalContent;
    }
    this.style.display = 'contents';
  }

  bindEvents() {
    // Thumbnail click events
    const thumbs = this.querySelectorAll('.mobile-gallery__thumb');
    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        this.setActiveImage(index);
      });
    });

    // Main image scroll events
    const mainContainer = this.querySelector('.mobile-gallery__main');
    if (mainContainer) {
      mainContainer.addEventListener('scroll', () => {
        if (this.isScrolling) return;

        const scrollLeft = mainContainer.scrollLeft;
        const itemWidth = mainContainer.clientWidth;
        const newIndex = Math.round(scrollLeft / itemWidth);

        if (newIndex !== this.currentIndex) {
          this.currentIndex = newIndex;
          this.updateActiveThumbnail();
        }
      });
    }
  }

  setActiveImage(index) {
    this.isScrolling = true;
    this.currentIndex = index;

    const mainContainer = this.querySelector('.mobile-gallery__main');
    // Guard clause in case called during destruction
    if (!mainContainer) return;

    const targetScrollLeft = index * mainContainer.clientWidth;

    mainContainer.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth'
    });

    this.updateActiveStates();

    setTimeout(() => {
      this.isScrolling = false;
    }, 300);
  }

  updateActiveThumbnail() {
    const thumbs = this.querySelectorAll('.mobile-gallery__thumb');
    thumbs.forEach((thumb, index) => {
      thumb.classList.toggle('active', index === this.currentIndex);
    });
    this.scrollThumbnailsToActive();
  }

  updateActiveStates() {
    const mainItems = this.querySelectorAll('.mobile-gallery__main-item');
    mainItems.forEach((item, index) => {
      const isActive = index === this.currentIndex;
      item.classList.toggle('active', isActive);
      item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
    this.updateActiveThumbnail();
  }

  scrollThumbnailsToActive() {
    const thumbsContainer = this.querySelector('.mobile-gallery__thumbs');
    const activeThumb = this.querySelector('.mobile-gallery__thumb.active');

    if (thumbsContainer && activeThumb) {
      const containerWidth = thumbsContainer.clientWidth;
      const thumbWidth = activeThumb.offsetWidth;
      const thumbLeft = activeThumb.offsetLeft;
      const scrollLeft = thumbLeft - (containerWidth / 2) + (thumbWidth / 2);

      thumbsContainer.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth'
      });
    }
  }

  handleVariantChange(e) {
    const variantImage = e.detail.variantImage;
    if (!variantImage) {
      this.resetGallery();
      return;
    }

    const getCleanFileName = (url) => {
      if (!url) return '';
      return url.split('?')[0].split('/').pop().replace(/(_\d+x\d*)/, '').replace(/(_master)/, '');
    };

    const targetFileName = getCleanFileName(variantImage);
    const isMobile = window.innerWidth <= 767;

    if (isMobile) {
      // --- MOBILE LOGIC (works with slider) ---
      const images = this.querySelectorAll('.mobile-gallery__main-item img');
      let matchedIndex = -1;

      for (let i = 0; i < images.length; i++) {
        const currentFileName = getCleanFileName(images[i].src);
        if (currentFileName && (currentFileName.includes(targetFileName) || targetFileName.includes(currentFileName))) {
          const mainItem = images[i].closest('.mobile-gallery__main-item');
          if (mainItem && mainItem.dataset.index) {
            matchedIndex = parseInt(mainItem.dataset.index);
            break;
          }
        }
      }

      if (matchedIndex > -1) {
        this.setActiveImage(matchedIndex);
      } else {
        // Fallback: replace first slide
        const firstMainItem = this.querySelector('.mobile-gallery__main-item[data-index="0"]');
        if (firstMainItem) {
          const img = firstMainItem.querySelector('img');
          const video = firstMainItem.querySelector('video, iframe');

          if (img && !video) {
            if (!img.dataset.originalSrc) {
              img.dataset.originalSrc = img.src;
              if (img.srcset) img.dataset.originalSrcset = img.srcset;
            }
            img.src = variantImage;
            img.removeAttribute('srcset');

            // Update thumb
            const firstThumb = this.querySelector('.mobile-gallery__thumb[data-index="0"] img');
            if (firstThumb) {
              if (!firstThumb.dataset.originalSrc) {
                firstThumb.dataset.originalSrc = firstThumb.src;
              }
              firstThumb.src = variantImage;
              firstThumb.removeAttribute('srcset');
            }
            this.setActiveImage(0);
          }
        }
      }

    } else {
      // --- DESKTOP LOGIC (works with original DOM) ---
      const originalHero = this.querySelector('.media-item:not(.variant-hero)');
      if (!originalHero) return;

      let variantHero = this.querySelector('.media-item.variant-hero');

      if (!variantHero) {
        variantHero = originalHero.cloneNode(true);
        variantHero.classList.add('variant-hero');
        variantHero.dataset.mediaId = 'variant-preview';
        originalHero.parentNode.insertBefore(variantHero, originalHero);
      }

      const video = variantHero.querySelector('video, iframe, model-viewer');
      if (video) video.remove();

      let img = variantHero.querySelector('img');
      if (!img) {
        const newImg = document.createElement('img');
        newImg.classList.add('product-image');
        variantHero.appendChild(newImg);
        img = newImg;
      }

      if (img) {
        img.src = variantImage;
        img.removeAttribute('srcset');
        img.alt = e.detail.variantTitle || 'Variant Image';
        img.style.display = 'block';
      }

      variantHero.style.display = 'block';
      originalHero.style.display = 'none';
    }
  }

  resetGallery() {
    // Desktop Reset
    const variantHero = this.querySelector('.media-item.variant-hero');
    const originalHero = this.querySelector('.media-item:not(.variant-hero)');

    if (variantHero) variantHero.remove();
    if (originalHero) originalHero.style.display = '';

    // Mobile Reset
    const images = this.querySelectorAll('.mobile-gallery__main-item img');
    images.forEach(img => {
      if (img.dataset.originalSrc) {
        img.src = img.dataset.originalSrc;
        if (img.dataset.originalSrcset) img.srcset = img.dataset.originalSrcset;
        delete img.dataset.originalSrc;
        delete img.dataset.originalSrcset;
      }
    });

    const thumbs = this.querySelectorAll('.mobile-gallery__thumb img');
    thumbs.forEach(img => {
      if (img.dataset.originalSrc) {
        img.src = img.dataset.originalSrc;
        delete img.dataset.originalSrc;
      }
    });
  }
}

// Global initialization
document.addEventListener('DOMContentLoaded', () => {
  // Define element only if not defined (prevents errors on hot reload)
  if (!customElements.get('mobile-gallery')) {
    customElements.define('mobile-gallery', MobileGallery);
  }

  window.addEventListener('purchase-option:change', (e) => {
    const galleries = document.querySelectorAll('mobile-gallery');
    galleries.forEach(gallery => {
      if (gallery && typeof gallery.handleVariantChange === 'function') {
        gallery.handleVariantChange(e);
      }
    });
  });
});